import type { CompanyFromTagSkipReason } from "@shaarp/common";
import type { TFunction } from "i18next";

/**
 * Why a company did not join the list: what `POST /company/from-tag` answered
 * for it (homonyms, the day's budget spent, a failure worth retrying), or
 * `not_identified` when nothing names it well enough to look it up.
 */
export type CompanySkipReason = CompanyFromTagSkipReason | "not_identified";

/** A company the caller could not place, named when the caller can name it. */
export type SkippedCompany = {
  readonly name?: string;
  readonly reason: CompanySkipReason;
};

/** The companies a "Save to a list" action attaches, and those it left out. */
export type ResolvedCompanies = {
  readonly companiesIds: readonly string[];
  /** Companies the caller could not place, each with why — reported so a
   *  partial add is never presented as a complete one, and the person knows
   *  whether trying again tomorrow, later or never will help. */
  readonly skipped: readonly SkippedCompany[];
};

// A wide table can cite dozens of companies; naming all of them would make the
// toast unreadable, so only the first few are spelled out.
const MAX_LISTED_SKIPPED_NAMES = 5;

// " · " rather than ", ": a comma can legitimately appear inside a company name
// (e.g. "Acme, Inc."), which would blend into the list and make four companies
// look like five.
const NAME_LIST_SEPARATOR = " · ";

/**
 * The sentence of one reason, by the key prefix it is written under: the day
 * spent first — it says when to come back — then a failure worth retrying,
 * then the companies nothing identifies.
 */
const SKIP_SENTENCES: readonly {
  readonly key: string;
  readonly reasons: readonly CompanySkipReason[];
}[] = [
  { key: "skippedRateLimited", reasons: ["rate_limited"] },
  { key: "skippedUnavailable", reasons: ["unavailable"] },
  { key: "skipped", reasons: ["homonyms", "not_identified"] },
];

/** `names`, the first `MAX_LISTED_SKIPPED_NAMES` spelled out and a "+N" for the rest. */
function nameList(names: readonly string[], t: TFunction): string {
  const shown = names
    .slice(0, MAX_LISTED_SKIPPED_NAMES)
    .join(NAME_LIST_SEPARATOR);
  const remaining = names.length - MAX_LISTED_SKIPPED_NAMES;
  return remaining > 0
    ? `${shown}${NAME_LIST_SEPARATOR}${t(
        "components.companiesListingAction.andMoreNames",
        { count: remaining }
      )}`
    : shown;
}

/**
 * The "not saved" lines of the toast: one sentence per reason — "{{count}}
 * not identified", "{{count}} unavailable, try again later", "{{count}} not
 * saved, try again tomorrow" — each naming the companies when the caller
 * could, truncated to the first `MAX_LISTED_SKIPPED_NAMES` followed by a "+N"
 * suffix. Empty when nothing was left out.
 */
export function formatSkippedDescription(
  skipped: readonly SkippedCompany[],
  t: TFunction
): string {
  return SKIP_SENTENCES.flatMap(({ key, reasons }) => {
    const companies = skipped.filter((company) =>
      reasons.includes(company.reason)
    );
    if (companies.length === 0) return [];
    const count = companies.length;
    const names = companies.flatMap((company) =>
      company.name ? [company.name] : []
    );
    return [
      names.length === 0
        ? t(`components.companiesListingAction.${key}`, { count })
        : t(`components.companiesListingAction.${key}WithNames`, {
            count,
            names: nameList(names, t),
          }),
    ];
  }).join(" ");
}
